import { useEffect, useId, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

/** The unsaved-changes guard (MS-819 shared primitives, C-24; decided by the
 * PO 2026-10-07 via MS-818 OQ-6). Ports the prototype's global exit-path
 * guard — `confirmDiscardIfDirty` plus its `beforeunload` listener
 * (subscription-portal.html, "Global exit-path guard") — onto the router:
 *
 * - in-app navigation, which includes a product-tab switch now that tabs are
 *   routes (MS-818): React Router's `useBlocker` asks
 *   `common.unsavedChangesConfirm` and stays put on Cancel;
 * - refresh / tab close: the browser's native "Leave site?" prompt;
 * - Sign out: AppShell calls `confirmDiscardIfDirty` before logging out (the
 *   prototype lists logout among the guard's callers) — sign-out is not a
 *   route change, so `useBlocker` alone would not see it.
 *
 * A page passes whether it has unsaved edits and how to throw them away;
 * MS-821 (Excess) and MS-822 (User Limits, including per-row edits) wire
 * theirs in. Accepting "Discard them?" really discards: the page stays
 * mounted across a product-tab switch (ProductTabRoute), so without it the
 * draft — and a later Save — would survive the "discarded" edits. */

interface Guard {
  isDirty: () => boolean
  discard: () => void
}

/** Every mounted guard, keyed by the guard's id: the prototype's
 * `window.__pageControllers` registry. Each entry reads the page's live
 * state, so a page that is still dirty is always reported as dirty. */
const guards = new Map<string, Guard>()

/** True while any mounted page reports unsaved edits. */
export function anyUnsavedChanges(): boolean {
  for (const guard of guards.values()) if (guard.isDirty()) return true
  return false
}

function discardAll(): void {
  for (const guard of guards.values()) if (guard.isDirty()) guard.discard()
}

/** The prototype's `confirmDiscardIfDirty`: true when it is safe to proceed —
 * nothing is dirty, or the admin accepted discarding (`message` is the
 * already-translated `common.unsavedChangesConfirm`). Accepting discards
 * every dirty page's edits (the prototype's `clearDirty()`), so the
 * navigation that follows is not asked about a second time. */
export function confirmDiscardIfDirty(message: string): boolean {
  if (!anyUnsavedChanges()) return true
  if (!window.confirm(message)) return false
  discardAll()
  return true
}

export function useUnsavedGuard(isDirty: boolean, onDiscard: () => void): void {
  const { t } = useTranslation()
  const id = useId()
  const dirtyRef = useRef(isDirty)
  const discardRef = useRef(onDiscard)
  useEffect(() => {
    dirtyRef.current = isDirty
    discardRef.current = onDiscard
  })

  useEffect(() => {
    guards.set(id, {
      isDirty: () => dirtyRef.current,
      discard: () => {
        discardRef.current()
        // Clean at once, not on the page's next render: the navigation that
        // follows an accepted discard is checked before that render.
        dirtyRef.current = false
      },
    })
    return () => {
      guards.delete(id)
    }
  }, [id])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyRef.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  )
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm(t('common.unsavedChangesConfirm'))) {
      guards.get(id)?.discard()
      blocker.proceed()
    } else {
      blocker.reset()
    }
  }, [blocker, id, t])

  useEffect(() => {
    if (!isDirty) return
    function onBeforeUnload(e: BeforeUnloadEvent) {
      // preventDefault alone asks for the browser's own "leave site?" prompt.
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])
}
